/**
 * The Members screen is the pool screen's first tab, `?tab=members` on the
 * existing `/pools` route, not a route file of its own.
 * `apps/app/src/routes/__root.tsx` holds the only `<Outlet />` in the routes
 * tree, so a `pools.something.tsx` child route would never paint. See
 * `plan.md` `## 5.16` for the full reasoning.
 *
 * It used to be `?view=members`. `aliasLegacyPoolView` keeps every link that
 * still carries that address resolving here (ISSUE-952).
 *
 * Every screen and every navigation reads and writes this URL state through
 * the four functions below, never through `search.tab`, `search.sheet` or
 * `search.memberId` directly.
 */
import {
  openPoolTabSearch,
  readPoolTab,
  type typePoolTab,
} from "../poolScreen/poolScreenRoute";

export const POOL_MEMBERS_TAB: typePoolTab = "members";

export const MEMBER_ACTIONS_SHEET_NAME = "member-actions";

export function isPoolMembersOpen(search: {
  poolId?: string | undefined;
  tab?: string | undefined;
}): boolean {
  return (
    readPoolTab(search) === POOL_MEMBERS_TAB &&
    typeof search.poolId === "string" &&
    search.poolId !== ""
  );
}

export function readMemberActionsTarget(search: {
  sheet?: string | undefined;
  memberId?: string | undefined;
}): string | null {
  if (search.sheet !== MEMBER_ACTIONS_SHEET_NAME) return null;
  if (typeof search.memberId !== "string" || search.memberId === "") {
    return null;
  }
  return search.memberId;
}

/** The pool member menu's Report flow (ISSUE-1045), opened in place of the member actions sheet. */
export const REPORT_MEMBER_SHEET_NAME = "report-member";

/** The member a report is being written about, or null. */
export function readReportMemberTarget(search: {
  sheet?: string | undefined;
  memberId?: string | undefined;
}): string | null {
  if (search.sheet !== REPORT_MEMBER_SHEET_NAME) return null;
  if (typeof search.memberId !== "string" || search.memberId === "") {
    return null;
  }
  return search.memberId;
}

export function openPoolMembersSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
): TSearch & { tab: typePoolTab } {
  return openPoolTabSearch(search, POOL_MEMBERS_TAB);
}

export function closePoolMembersSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
): TSearch {
  const dropped = new Set<string>([
    "view",
    "tab",
    "sheet",
    "memberId",
    "memberQ",
  ]);
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !dropped.has(key)),
  ) as TSearch;
}
